/**
 * DAG fragments for speaker separation.
 *
 * The graph was written for Veritone Speaker Separation V3F — "Veritone -
 * Speaker Separation V3", a.k.a. Speaker Detection V3F, engine
 * `02f4d710-cbcd-4534-98d6-a31522f8f4a6`, from
 * https://github.com/veritone/task-veritone-speaker-detection (model library:
 * https://github.com/veritone/ds-veritone-speaker-detection) — and mirrors the
 * reference DAG in that repo's README: the ingestor emits one chunk covering
 * the whole audio (separation needs the full timeline to cluster voices) and
 * the engine hangs off it in parallel with transcription.
 *
 * Both engines sit in the "speaker detection" category
 * `a856c447-1030-4fb0-917f-08179f949c4e`, which is how results are read back,
 * so the graph shape carries over. The CONFIGURED engine is now Speechmatics
 * (see `speakerSeparationEngineId` in engines/selectors.ts); the payload
 * follows it through `speakerSeparationPayload`, which keeps one shape per
 * engine family because each engine silently ignores the other's parameters.
 *
 * Everything here is data, not GraphQL text. Both callers — the upload DAG in
 * engineJobHelper and the standalone job in the speaker-separation duck —
 * build from these builders and send the result as a `CreateJob` variable.
 */
import { DAG_REFERENCE_IDS } from './constants';

/** Minimal createJob task shape (kept local to avoid circular import with engineJobHelper). */
export interface SpeakerSeparationJobTask {
  engineId: string;
  payload?: Record<string, unknown>;
  executionPreferences?: {
    priority?: number;
    parentCompleteBeforeStarting?: boolean;
    maxEngines?: number;
  };
  ioFolders?: Array<{
    referenceId: string;
    mode: string;
    type: string;
  }>;
}

export interface SpeakerSeparationRoute {
  parentIoFolderReferenceId: string;
  childIoFolderReferenceId: string;
  options: Record<string, never>;
}

/** GLC ingestor outputs a single full-audio chunk (not 3 min sub-chunks). */
export const fullAudioIngestionTask = ({
  glcIngestionEngineId,
  url,
}: {
  glcIngestionEngineId: string;
  url: string;
}): SpeakerSeparationJobTask => ({
  engineId: glcIngestionEngineId,
  payload: {
    app: 'redact',
    chunkType: 'audio',
    url,
    playback: false,
    zeropadOffsetAudio: true,
  },
  executionPreferences: {
    parentCompleteBeforeStarting: true,
    priority: -20,
  },
  ioFolders: [
    {
      referenceId: 'ingestOutputFolder',
      mode: 'chunk',
      type: 'output',
    },
  ],
});

/**
 * Payload for the configured speaker-separation engine.
 *
 * The two engines take different parameters for the same job, and each ignores
 * the other's, so sending one shape to the other engine runs it silently at its
 * own defaults rather than failing. Hence a map rather than one literal.
 *
 * TODO: these are hardcoded tuning values. They should move into user-adjustable
 * settings (alongside the other engine settings in global settings) so speaker
 * sensitivity can be tuned per media without a redeploy.
 */

/** Veritone Speaker Separation V3F. Speechmatics has 500+ ids (language x
 * version x tier) and is the engine we are moving to, so it is the default and
 * only the Veritone ones are listed. */
const VERITONE_SPEAKER_SEPARATION_ENGINE_IDS = new Set([
  '02f4d710-cbcd-4534-98d6-a31522f8f4a6',
]);

export const speakerSeparationEngineFamily = (engineId?: string) =>
  engineId && VERITONE_SPEAKER_SEPARATION_ENGINE_IDS.has(engineId)
    ? ('veritone' as const)
    : ('speechmatics' as const);

/**
 * Veritone reads the transcript we already produced and clusters its words, so
 * it needs pointing at that transcript. Values are the engine's own defaults,
 * sent explicitly so the configuration is visible here and does not move under
 * us if the engine's defaults change. See
 * `veritone/task-veritone-speaker-detection` for the full list.
 *
 * `diarization` / `speakerChangeSensitivity` do not belong here — this engine
 * ignores them; `stoppingThreshold` is its equivalent dial.
 */
const veritoneSpeakerSeparationPayload = ({
  transcriptEngineId,
  transcriptCategoryId,
}: {
  transcriptEngineId?: string;
  transcriptCategoryId?: string;
}) => ({
  // Seed segmentation from transcript punctuation timing - materially better
  // speaker boundaries for transcript display, which is the whole point here.
  useTranscript: true,
  // Point the engine at the transcript we actually produced rather than letting
  // it fall back to its own hardcoded category id.
  ...(transcriptEngineId && { transcriptEngineId }),
  ...(transcriptCategoryId && { transcriptCategoryId }),
  // Agglomerative clustering stop threshold. Higher = more speakers.
  // Engine default 0.65, useful range 0.55-0.75.
  stoppingThreshold: 0.65,
  // Max speaker labels per chunk; overflow speakers are labelled "?".
  maxSpeakerLabels: 8,
});

/**
 * Speechmatics diarizes the audio itself — it does not read our transcript, so
 * none of the transcript ids apply. Its output is speaker time ranges, which
 * `mergeTranscriptionViewable` maps onto the transcript's words by midpoint.
 *
 * `diarization` defaults to `none` (see the engine's config.yaml), so it has to
 * be asked for. `speaker` identifies who is speaking, which is what grouping a
 * transcript by speaker needs; `speaker_change` only marks that a change
 * happened, with no identity to group by.
 *
 * `speakerChangeSensitivity` is deliberately absent: the engine's own profile
 * says it is read only when diarization is `speaker_change`, so under `speaker`
 * it is a value that looks like a tuning dial and does nothing.
 */
const speechmaticsSpeakerSeparationPayload = () => ({
  diarization: 'speaker',
});

export const speakerSeparationPayload = ({
  engineId,
  transcriptEngineId,
  transcriptCategoryId,
}: {
  engineId?: string;
  transcriptEngineId?: string;
  transcriptCategoryId?: string;
}) => ({
  app: 'redact',
  ...(speakerSeparationEngineFamily(engineId) === 'veritone'
    ? veritoneSpeakerSeparationPayload({
        transcriptEngineId,
        transcriptCategoryId,
      })
    : speechmaticsSpeakerSeparationPayload()),
});

export const speakerSeparationEngineTask = (
  speakerSeparationEngineId: string,
  {
    transcriptEngineId,
    transcriptCategoryId,
    ...extraPayload
  }: {
    transcriptEngineId?: string;
    transcriptCategoryId?: string;
    /** Anything else the caller puts on the engine payload, e.g. `user`. */
    [key: string]: unknown;
  } = {}
): SpeakerSeparationJobTask => ({
  engineId: speakerSeparationEngineId,
  payload: {
    ...speakerSeparationPayload({
      engineId: speakerSeparationEngineId,
      transcriptEngineId,
      transcriptCategoryId,
    }),
    ...extraPayload,
  },
  executionPreferences: {
    maxEngines: 10,
    parentCompleteBeforeStarting: true,
    priority: -5,
  },
  ioFolders: [
    {
      referenceId: 'ssInputFolder',
      mode: 'chunk',
      type: 'input',
    },
    {
      referenceId: 'ssOutputFolder',
      mode: 'chunk',
      type: 'output',
    },
  ],
});

export const speakerSeparationOutputWriterTask = (
  outputWriterEngineId: string
): SpeakerSeparationJobTask => ({
  engineId: outputWriterEngineId,
  executionPreferences: {
    parentCompleteBeforeStarting: true,
    priority: -15,
  },
  ioFolders: [
    {
      referenceId: DAG_REFERENCE_IDS['owFromSpeakerSeparation'],
      mode: 'chunk',
      type: 'input',
    },
  ],
});

/**
 * The whole standalone job: ingest the audio, separate it, write the output.
 *
 * Run from the editor, in parallel with anything else the user asked for — as
 * opposed to the upload path, which folds the same three tasks into the
 * ingestion job alongside transcription and detection.
 *
 * It does not wait for a transcript. Speechmatics, which is what the config
 * points at, diarizes the audio itself and never reads one. The Veritone
 * engine does need one and waits for it itself, which is what `useTranscript`
 * in its payload is for — so neither engine needs the app to serialise the two
 * jobs, and doing so only made the user wait.
 *
 * This used to be an interpolated GraphQL string in the speaker-separation
 * duck, which duplicated every ioFolder name and priority below and had
 * already drifted from them on the transcript ids (review B7). Returning the
 * `CreateJob` input as data keeps one definition and lets the caller send it
 * as a query variable, so no engine id or signed URL is spliced into query
 * text.
 */
export const speakerSeparationJobInput = ({
  targetId,
  clusterId,
  url,
  glcIngestionEngineId,
  speakerSeparationEngineId,
  outputWriterEngineId,
  transcriptEngineId,
  transcriptCategoryId,
  user,
}: {
  targetId: string;
  clusterId?: string;
  url: string;
  glcIngestionEngineId: string;
  speakerSeparationEngineId: string;
  outputWriterEngineId: string;
  transcriptEngineId?: string;
  transcriptCategoryId?: string;
  user?: string;
}) => ({
  targetId,
  clusterId: clusterId ?? '',
  tasks: [
    fullAudioIngestionTask({ glcIngestionEngineId, url }),
    speakerSeparationEngineTask(speakerSeparationEngineId, {
      transcriptEngineId,
      transcriptCategoryId,
      // The engine reads the media from the input chunk; these ride along for
      // the platform's own bookkeeping, as they did before this was data.
      targetId,
      url,
      ...(user && { user }),
    }),
    speakerSeparationOutputWriterTask(outputWriterEngineId),
  ],
  routes: speakerSeparationIngestRoutes(),
});

/**
 * The engine needs one chunk covering the whole audio. That normally comes
 * straight from the ingestor, but when object detection runs in the same job
 * the ingestor has to emit video, so a single-audio-chunk fast chunker is
 * inserted in front and named here instead.
 */
export const speakerSeparationIngestRoutes = (
  audioSourceFolderReferenceId = 'ingestOutputFolder'
): SpeakerSeparationRoute[] => [
  {
    parentIoFolderReferenceId: audioSourceFolderReferenceId,
    childIoFolderReferenceId: 'ssInputFolder',
    options: {},
  },
  {
    parentIoFolderReferenceId: 'ssOutputFolder',
    childIoFolderReferenceId: DAG_REFERENCE_IDS['owFromSpeakerSeparation'],
    options: {},
  },
];
